import { useMemo, useState } from 'react';
import { IVariable } from '../../model';
import { TemplateVariables } from 'crono-fe-common/types/template';
import { TemplateVariablesSelectorWrapper } from './style';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import { getExternalPropertyForVariables } from 'utils/businessLogic';
import { Divider, Tooltip, Typography } from '@mui/material';
import IconRightArrow from 'crono-fe-common/icons/Icon-Right-Arrow';
import { BlurBackgroundDivFixed } from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import { createPortal } from 'react-dom';
import ExternalPropertyTemplateVariablesSelector from './externalPropertiesTemplateVariablesSelector';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import useGetTemplateVariables from 'hooks/services/variables/useGetTemplateVariables';
import { ITemplateVariables } from 'crono-fe-common/types/templateVariables';
import { colors } from 'crono-fe-common/theme';
import { useNavigate } from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import { useConfirmModal } from 'context/confirmModal';
import AiStarsBadge from 'components/AiStarsBadge';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import { ReactComponent as EyeShow } from 'crono-fe-common/icons/EyeShow.svg';
import {
  createVariablesFromTemplateVariables,
  templateVariables as templateVariablesArray,
} from '../../template.service';

interface IProps {
  addVariables: (variable: TemplateVariables | string) => void;
  tooltipToRight?: boolean;
}

const VariablesSelector = ({
  addVariables,
  tooltipToRight = false,
}: IProps) => {
  const { isManager } = useRoleCapabilities();

  const { data: externalProperties } = useGetExternalProperty({
    tableType: null,
    onlyTag: null,
  });

  const sortedExternalProperties = useMemo(() => {
    if (!externalProperties?.data?.data) return [];
    //First the ones with aiVariablesSettings
    return externalProperties.data.data.sort(
      (a: ExternalProperty, b: ExternalProperty) => {
        return a.aiVariableSettings ? -1 : b.aiVariableSettings ? 1 : 0;
      },
    );
  }, [externalProperties]);

  const { data: templateVariables } = useGetTemplateVariables();

  const VariablesToShow: IVariable[] = useMemo(() => {
    const templateVars: ITemplateVariables | undefined =
      templateVariables?.data?.data;

    return [
      ...templateVariablesArray,
      ...getExternalPropertyForVariables(sortedExternalProperties),
      ...createVariablesFromTemplateVariables(templateVars),
    ];
  }, [sortedExternalProperties, templateVariables]);

  const [showOtherVariables, setShowOtherVariables] = useState(false);

  const { openModal } = useConfirmModal();

  const navigate = useNavigate();

  const redirectTemplateVariables = () => {
    openModal({
      title: 'Leave this page?',
      text: 'Your current job in this page will be lost. Are you sure you want to leave?',
      confirmText: 'Leave',
      confirmFunction: () => {
        navigate(PATH.SETTINGS.TEMPLATE_VARIABLES);
      },
      cancelText: 'Cancel',
    });
  };

  return (
    <>
      {showOtherVariables &&
        //Portal to escape the tooltip
        createPortal(
          <BlurBackgroundDivFixed
            onClick={() => setShowOtherVariables(false)}
            style={{ zIndex: 1000000 }}
          >
            <ExternalPropertyTemplateVariablesSelector
              externalProperties={sortedExternalProperties}
              setShowOtherVariables={setShowOtherVariables}
            />
          </BlurBackgroundDivFixed>,
          document.body,
        )}
      <TemplateVariablesSelectorWrapper>
        {VariablesToShow.map((variable, index) => {
          const disabled =
            'userBased' in variable
              ? variable.userBased && !variable.content
              : false;

          return (
            <Tooltip
              arrow
              enterDelay={400}
              enterNextDelay={400}
              key={index}
              placement={
                disabled ? 'bottom' : tooltipToRight ? 'right' : 'left'
              }
              title={
                disabled ? (
                  <div
                    style={{
                      whiteSpace: 'pre-line',
                      textAlign: 'center',
                    }}
                  >
                    This snippet is missing a text. Please{' '}
                    <a
                      style={{
                        cursor: 'pointer',
                        color: colors.primaryDark,
                      }}
                      onClick={redirectTemplateVariables}
                    >
                      click here
                    </a>{' '}
                    to add a text to use it.
                  </div>
                ) : 'content' in variable && variable.content ? (
                  <div>{variable.content}</div>
                ) : null
              }
            >
              <div
                key={variable.text}
                className={`variable-item ${disabled ? 'disabled' : ''}`}
                onClick={() => {
                  if (!disabled) addVariables(variable.variable);
                }}
              >
                <span className="text-left">{variable.text} </span>
                {variable.isAiVariable && <AiStarsBadge />}{' '}
                {'content' in variable && !disabled && (
                  <EyeShow className="eye-icon" />
                )}
              </div>
            </Tooltip>
          );
        })}
        {isManager && (
          <>
            <Divider />
            <div
              className="variable-item other-variables"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowOtherVariables(true);
              }}
            >
              <Typography variant="inherit">Other Variables</Typography>
              <IconRightArrow style={{ width: 16, height: 16 }} />
            </div>
          </>
        )}
      </TemplateVariablesSelectorWrapper>
    </>
  );
};

export default VariablesSelector;
